import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { Check, Eye, Image as ImageIcon, Scissors, Sparkles, Wand2 } from "lucide-react";
import type { AssetKind } from "../../assets/mapping.js";
import type { StyleMeta } from "../../api/client.js";
import { Button, Textarea } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";

const S = {
  title: { zh: "AI 素材助手", en: "AI asset assistant" },
  subtitle: { zh: "在这里选风格、让 AI 写提示词并生成；完成后会自动绑定到当前画面。", en: "Choose a style, draft the prompt, and generate here. The result binds to this frame automatically." },
  stepStyle: { zh: "1 · 选择风格", en: "1 · Choose a style" },
  stepPrompt: { zh: "2 · 描述画面", en: "2 · Describe the image" },
  stepGenerate: { zh: "3 · 生成并绑定", en: "3 · Generate and bind" },
  aiDraft: { zh: "AI 帮我写", en: "Ask AI to draft" },
  drafting: { zh: "AI 正在组织提示词…", en: "AI is drafting the prompt…" },
  generate: { zh: "生成并放进画面", en: "Generate and place in frame" },
  generating: { zh: "正在生成图片，通常需要 1–2 分钟…", en: "Generating image; this normally takes 1–2 minutes…" },
  done: { zh: "已生成并绑定，左侧画面会自动更新。", en: "Generated and bound. The stage updates automatically." },
  unavailable: { zh: "登录且账号具备生图权限后，可在这里直接生成。", en: "Sign in with image-generation access to create art here." },
  error: { zh: "生成失败，提示词和风格已保留，可以直接重试。", en: "Generation failed. Your style and prompt are preserved for retry." },
  promptLabel: { zh: "可编辑提示词", en: "Editable prompt" },
  promptHint: { zh: "这里只写画面内容；所选风格和最新 IDE 的灰底抠图协议会由服务端自动套用。", en: "Describe only the content. The server applies the selected style and latest IDE neutral-plate cutout contract." },
  noStyles: { zh: "使用项目默认风格", en: "Using project default style" },
  cutout: { zh: "自动抠图", en: "Auto cutout" },
  cutting: { zh: "正在放大并抠除背景，通常约 1 分钟…", en: "Upscaling and removing the background; usually about a minute…" },
  cutoutHint: { zh: "角色已绑定图片。抠图成功后才会替换当前素材，失败不会影响原图。", en: "A character image is bound. The transparent result replaces it only after success; failures keep the original." },
  cutoutDone: { zh: "自动抠图完成，透明立绘已替换原图。", en: "Cutout complete. The transparent sprite has replaced the original." },
  finalPrompt: { zh: "查看最终提示词", en: "Preview final prompt" },
  finalPromptHint: { zh: "这是服务端按最新 IDE 模板实际提交的完整提示词。", en: "This is the complete prompt the server submits using the latest IDE template." },
} satisfies Record<string, Entry>;

export interface StudioAssetTarget {
  readonly kind: Extract<AssetKind, "background" | "character" | "cg">;
  readonly symbol: string;
  readonly charId?: string;
}

export interface StudioAssetAssistantProps {
  readonly target: StudioAssetTarget;
  readonly styles: readonly StyleMeta[];
  readonly enabled: boolean;
  readonly busy?: boolean;
  readonly onSuggest?: (args: { kind: AssetKind; name: string; styleName?: string; styleDescription?: string; notes?: string }) => Promise<string>;
  readonly onGenerate: (args: { kind: AssetKind; symbol: string; charId?: string; prompt: string; family?: string; referenceAssetIds?: string[] }) => Promise<void> | void;
  readonly currentAssetId?: string;
  readonly referenceAssetId?: string;
  readonly currentAssetUrl?: string;
  readonly cutoutEnabled?: boolean;
  readonly onCutout?: () => Promise<void> | void;
  readonly onPreviewPrompt?: (args: { kind: "background" | "character" | "cg"; prompt: string; family?: string; symbol?: string; charId?: string; hasAssetReference?: boolean }) => Promise<string>;
}

function seed(target: StudioAssetTarget): string {
  const symbol = target.symbol.replace(/[_-]+/g, " ");
  if (target.kind === "background") return `${symbol}, visual novel environment, no characters`;
  if (target.kind === "character") return `${target.charId ?? "character"}, full-body pose and expression: ${symbol}`;
  return `${symbol}, emotional full-screen key illustration`;
}

export function StudioAssetAssistant({ target, styles, enabled, busy, onSuggest, onGenerate, currentAssetId, referenceAssetId, currentAssetUrl, cutoutEnabled, onCutout, onPreviewPrompt }: StudioAssetAssistantProps): ReactElement {
  const tr = useTr();
  const key = `${target.kind}:${target.charId ?? ""}:${target.symbol}`;
  const activeKey = useRef(key);
  const requestVersion = useRef(0);
  activeKey.current = key;
  const availableStyles = useMemo(() => styles.filter((style) => (style.kinds as readonly string[]).includes(target.kind)), [styles, target.kind]);
  const styleKey = availableStyles.map((style) => style.id).join("|");
  const [family, setFamily] = useState(availableStyles[0]?.id ?? "");
  const [prompt, setPrompt] = useState(() => seed(target));
  const [status, setStatus] = useState<"idle" | "drafting" | "generating" | "cutting" | "done" | "cutout-done" | "error">("idle");
  const [finalPrompt, setFinalPrompt] = useState("");
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    requestVersion.current += 1;
    setPrompt(seed(target));
    setStatus("idle");
    setFinalPrompt("");
    setPreviewing(false);
    setFamily((current) => availableStyles.some((style) => style.id === current) ? current : (availableStyles[0]?.id ?? ""));
  }, [key, styleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedStyle = availableStyles.find((style) => style.id === family);
  const suggest = async () => {
    if (!onSuggest || !enabled || status === "drafting" || status === "generating") return;
    setStatus("drafting");
    const requestKey = key;
    const version = ++requestVersion.current;
    try {
      const next = await onSuggest({ kind: target.kind, name: target.symbol, styleName: selectedStyle?.name, styleDescription: selectedStyle?.description, notes: prompt.trim() || undefined });
      if (activeKey.current !== requestKey || requestVersion.current !== version) return;
      if (next.trim()) { setPrompt(next.trim()); setFinalPrompt(""); }
      setStatus("idle");
    } catch {
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("error");
    }
  };
  const generate = async () => {
    if (!enabled || busy || !prompt.trim() || status === "generating") return;
    setStatus("generating");
    const requestKey = key;
    const version = ++requestVersion.current;
    try {
      await onGenerate({ kind: target.kind, symbol: target.symbol, charId: target.charId, prompt: prompt.trim(), family: family || undefined, referenceAssetIds: referenceAssetId ? [referenceAssetId] : undefined });
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("done");
    } catch {
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("error");
    }
  };
  const cutout = async () => {
    if (!onCutout || !cutoutEnabled || busy || status === "cutting") return;
    setStatus("cutting");
    const requestKey = key;
    const version = ++requestVersion.current;
    try {
      await onCutout();
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("cutout-done");
    } catch {
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("error");
    }
  };
  const previewFinalPrompt = async () => {
    if (!onPreviewPrompt || !prompt.trim() || previewing) return;
    setPreviewing(true);
    const requestKey = key;
    const version = ++requestVersion.current;
    try {
      const next = await onPreviewPrompt({ kind: target.kind, prompt: prompt.trim(), family: family || undefined, symbol: target.symbol, charId: target.charId, hasAssetReference: Boolean(referenceAssetId) });
      if (activeKey.current === requestKey && requestVersion.current === version) setFinalPrompt(next);
    } catch {
      if (activeKey.current === requestKey && requestVersion.current === version) setStatus("error");
    } finally {
      if (activeKey.current === requestKey && requestVersion.current === version) setPreviewing(false);
    }
  };

  return (
    <section className="studio-asset-assistant" aria-label={tr(S.title)}>
      <header><span><Sparkles size={16} />{tr(S.title)}</span><small>{tr(S.subtitle)}</small></header>
      <div className="studio-asset-assistant__step"><strong>{tr(S.stepStyle)}</strong>
        {availableStyles.length ? <div className="studio-asset-assistant__styles" role="radiogroup">
          {availableStyles.map((style) => <button key={style.id} type="button" role="radio" aria-checked={family === style.id} data-active={family === style.id || undefined} disabled={!enabled || busy} onClick={() => { requestVersion.current += 1; setPreviewing(false); setFamily(style.id); setFinalPrompt(""); }}>
            <span>{style.previewUrl ? <img src={style.previewUrl} alt="" /> : <ImageIcon size={16} />}{family === style.id ? <i><Check size={10} /></i> : null}</span><small>{style.name}</small>
          </button>)}
        </div> : <p>{tr(S.noStyles)}</p>}
      </div>
      <div className="studio-asset-assistant__step">
        <div className="studio-asset-assistant__step-head"><strong>{tr(S.stepPrompt)}</strong>{onSuggest ? <Button variant="ghost" size="sm" loading={status === "drafting"} disabled={!enabled || busy || status === "generating"} leadingIcon={<Sparkles size={13} />} onClick={() => void suggest()}>{tr(S.aiDraft)}</Button> : null}</div>
        <Textarea aria-label={tr(S.promptLabel)} rows={4} value={prompt} disabled={!enabled || busy || status === "generating"} hint={tr(S.promptHint)} onChange={(event) => { requestVersion.current += 1; setPreviewing(false); setPrompt(event.currentTarget.value); setFinalPrompt(""); if (status === "done" || status === "error") setStatus("idle"); }} />
        {onPreviewPrompt ? <Button variant="ghost" size="sm" loading={previewing} leadingIcon={<Eye size={13} />} onClick={() => void previewFinalPrompt()}>{tr(S.finalPrompt)}</Button> : null}
        {finalPrompt ? <details className="studio-asset-assistant__final"><summary>{tr(S.finalPromptHint)}</summary><pre>{finalPrompt}</pre></details> : null}
      </div>
      <div className="studio-asset-assistant__step studio-asset-assistant__generate">
        <strong>{tr(S.stepGenerate)}</strong>
        <Button variant="primary" loading={status === "generating" || busy} disabled={!enabled || !prompt.trim() || status === "drafting" || status === "generating"} leadingIcon={<Wand2 size={15} />} onClick={() => void generate()}>{tr(S.generate)}</Button>
        <p role={status === "error" ? "alert" : "status"} data-state={status}>{tr(!enabled ? S.unavailable : status === "drafting" ? S.drafting : status === "generating" ? S.generating : status === "done" ? S.done : status === "error" ? S.error : S.promptHint)}</p>
      </div>
      {target.kind === "character" && currentAssetId ? (
        <div className="studio-asset-assistant__cutout">
          {currentAssetUrl ? <img src={currentAssetUrl} alt="" /> : <span><ImageIcon size={20} /></span>}
          <div><strong>{tr(S.cutout)}</strong><small>{tr(status === "cutting" ? S.cutting : status === "cutout-done" ? S.cutoutDone : S.cutoutHint)}</small></div>
          <Button variant="secondary" size="sm" loading={status === "cutting"} disabled={!cutoutEnabled || !onCutout || busy || status === "cutting"} leadingIcon={<Scissors size={14} />} onClick={() => void cutout()}>{tr(S.cutout)}</Button>
        </div>
      ) : null}
    </section>
  );
}
